import "./formDropdown.scss";

import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import { withScreenContext } from "../../components/screen/ScreenContext";
import KeyConstants from "../../constants/keyConstants";
import { PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import uniqueId from "../../utils/uniqueId";
import { withContainerContext } from "../container/ContainerContext";
import HelpIcon from "../interface/HelpIcon";
import Icon from "../interface/Icon";
import onPulseAnimationEnd from "../onPulseAnimationEnd";
import FormDropdownMenu, { renderOptionIcon } from "./FormDropdownMenu";
import { getDropdownOptions } from "./utils";

const SAFE_SPACE = "__SPACE__";
const SAFE_NULL = "__NULL__";

const isMobile = preprocessorQuery.isMobile();
const isDesktop = preprocessorQuery.isDesktop();

export const renderOptionLabel = (opt, noneLabel) =>
  opt ? getLabelWithUntitled(opt.label) : noneLabel ? noneLabel : "None";

const findSelectedOption = (options, value) => {
  let result = null;
  if (options) {
    options.find(opt => {
      if (opt.value === value) {
        result = opt;
        return true;
      } else if (opt.children) {
        result = findSelectedOption(opt.children, value);
        return Boolean(result);
      }
    });
  }
  return result;
};

class FormDropdown extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = { showMenu: false, options: null };
    this._domNode = null;
    this.getSelectedOption = this.getSelectedOption.bind(this);
    this.showMenu = this.showMenu.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.toggle = this.toggle.bind(this);
    this.onToggleMenu = this.onToggleMenu.bind(this); // Passing to menu component
    this.optionClick = this.optionClick.bind(this);

    this._dropdownId = "x" + uniqueId();
  }

  componentDidMount() {
    if (this.props.initialShowMenu) {
      this.showMenu();
    }
  }

  getSelectedOption(props) {
    const options = getDropdownOptions(props.options);
    return findSelectedOption(options, props.value);
  }

  toggle() {
    if (this.state.showMenu) {
      this.hideMenu();
    } else {
      this.showMenu();
    }
  }

  getListItemDomId(opt) {
    const val = opt.value ? `${opt.value.toString()}`.replace(/\s/g, SAFE_SPACE) : SAFE_NULL;
    return `${this._dropdownId}-${val}`;
  }

  onKeyDown(e) {
    if (!this.state.showMenu) {
      // Not currently open.
      // So open it with "enter"
      switch (e.key) {
        case KeyConstants.Enter:
          this.showMenu();
          break;
        default:
          break;
      }
    }
  }

  onBlur(_e) {
    // If we don't have a search field, then any blur means we are moving to another control.
    // if (!this.props.allowSearch) {
    //   this.selectHighlightedOption();
    // }
  }

  showMenu() {
    this.setState({
      showMenu: true
    });
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(true);
    }
  }

  hideMenu() {
    this.setState({ showMenu: false });
    if (this._domNode) {
      this._domNode.focus();
    }
    if (this.props.onToggleMenu) {
      this.props.onToggleMenu(false);
    }
  }

  optionClick(value) {
    this.hideMenu();
    const onChange = this.props.onChange;
    const oldValue = this.props.value;
    // In mobile mode, wait for the end of page sliding
    if (isMobile) {
      setTimeout(() => {
        if (value !== oldValue) {
          onChange(value);
        }
      }, 200);
    } else {
      if (value !== oldValue) {
        onChange(value);
      }
    }
  }

  focus() {
    this._domNode.focus();
    if (this.props.openOnFocus) {
      this.showMenu();
    }
  }

  onToggleMenu(value) {
    if (value) {
      this.showMenu();
    } else {
      this.hideMenu();
    }
  }

  getMenuStyle() {
    let style = this.props.style ? { ...this.props.style } : {};

    if (isDesktop) {
      style.width =
        this.props.useParentWidth && this._domNode && this._domNode.clientWidth
          ? `${this._domNode.clientWidth}px`
          : this.props.style && this.props.style.width
            ? this.props.style.width
            : `auto`;
    }

    return style;
  }

  renderPopover() {
    return (
      <FormDropdownMenu
        targetId={this._dropdownId}
        showMenu={this.state.showMenu}
        options={this.props.options}
        onOptionClick={this.optionClick}
        onToggleMenu={this.onToggleMenu}
        allowSearch={this.props.allowSearch}
        allowParentSelection={this.props.allowParentSelection}
        value={this.props.value}
        popoverClassName={this.props.popoverClassName}
        title={this.props.title}
        backText={this.props.backText}
        showDescription={this.props.showDescription}
        style={this.getMenuStyle()}
        disableTabLock={this.props.disableTabLock}
        alwaysPortal={this.props.alwaysPortal}
        showTick={this.props.showTick}
        focusOnOpen={true}
        preserveSpaces={this.props.preserveSpaces}
      />
    );
  }

  renderIconControl() {
    return (
      <Icon
        className="dropdownIconControl"
        onClick={this.props.disabled ? null : this.toggle}
        icon={this.props.controlIcon}
        color={this.props.controlIconColor || "mono"}
        id={this._dropdownId}
      />
    );
  }

  renderNormalControl() {
    const selectedOption = this.getSelectedOption(this.props);
    const itemLabel = renderOptionLabel(selectedOption, this.props.noneLabel);
    const arrowIcon = this.props.showArrow
      ? this.props.arrowIcon
        ? this.props.arrowIcon
        : isDesktop
          ? "downArrow"
          : this.props.alwaysPortal
            ? "rightArrow"
            : isMobile
              ? "rightArrow"
              : "downArrow"
      : null;

    return (
      <div
        className={`dropdownControl ${this.state.showMenu && !this.props.disabled ? "open" : "closed"}`}
        onClick={this.props.disabled ? null : this.toggle}
      >
        {selectedOption ? renderOptionIcon(selectedOption) : null}
        <div className="dropdownText">
          <div className={`dropdownLabel ${this.props.centerLabel ? "centered" : ""}`}>{itemLabel}</div>
        </div>
        <Icon className="downArrow" icon={arrowIcon} color="mono" />
      </div>
    );
  }

  render() {
    let className = ["formDropdown"];
    if (this.props.pulse) {
      className.push("pulse");
    }
    if (this.props.className) {
      className.push(this.props.className);
    }
    return (
      <div className={className.join(" ")} onAnimationEnd={onPulseAnimationEnd}>
        {this.props.label ? <span className="formDropdownLabel">{this.props.label}</span> : null}
        <div
          ref={node => (this._domNode = node)}
          className={`dropdown ${this.props.disabled ? "disabled" : ""}`}
          tabIndex={this.props.disabled ? -1 : this.props.tabIndex ? this.props.tabIndex : 0}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          id={this._dropdownId}
        >
          {this.props.controlIcon ? this.renderIconControl() : this.renderNormalControl()}
          {this.props.helpTooltip ? <HelpIcon tooltip={this.props.helpTooltip} /> : null}
          {this.renderPopover()}
        </div>
      </div>
    );
  }
}

FormDropdown.propTypes = {
  className: PropTypes.string,
  options: PropTypes.oneOfType([PropTypeShapes.listOptionArrayOrList, PropTypes.func]).isRequired,
  openOnFocus: PropTypes.bool,
  allowSearch: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  centerLabel: PropTypes.bool,
  preserveSpaces: PropTypes.bool,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  popoverClassName: PropTypes.string,
  onToggleMenu: PropTypes.func,
  initialShowMenu: PropTypes.bool,
  title: PropTypes.string,
  backText: PropTypes.string,
  showDescription: PropTypes.bool,
  useParentWidth: PropTypes.bool.isRequired,
  style: PropTypes.object,
  disableTabLock: PropTypes.bool,
  allowParentSelection: PropTypes.bool,
  noneLabel: PropTypes.string,
  showArrow: PropTypes.bool,
  helpTooltip: PropTypes.string,

  pulse: PropTypes.bool,

  //#!if iOS === true || harvard === true
  alwaysPortal: PropTypes.bool,
  controlIcon: PropTypes.string,
  controlIconColor: PropTypes.string,
  showTick: PropTypes.bool,
  arrowIcon: PropTypes.string,

  // From withScreenContext
  screenContext: PropTypes.object,

  // From withContainerContext
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
  //#!endif
};

FormDropdown.defaultProps = {
  showArrow: true,
  useParentWidth: true,
  showTick: true
};

let exportedView = FormDropdown;
//#!if iOS === true || harvard === true
exportedView = withContainerContext(withScreenContext(FormDropdown));
//#!endif
export default exportedView;
